import { ValidationOutput } from './validation.output.js';
import { initLogger } from '../../logger.js';
import { getErrorMessage } from '../../error-utils.js';
import { tryParseCalmCore } from './calm-core-parse.js';
/**
 * Validate all node details in an architecture by recursively loading and validating any
 * referenced detailed-architecture sub-architectures.
 *
 * The raw sub-architecture document is loaded through the shared {@link CachingTrackingResolver},
 * which caches each document and records which references have been visited. Cycle safety and
 * "validate each sub-architecture once" both come from that tracking, so a cyclic
 * detailed-architecture terminates instead of recursing unbounded.
 *
 * Pattern resolution priority per node:
 *  1. `details.required-pattern` URL
 *  2. `$schema` field of the detailed-architecture document
 *  3. Neither found → recursiveValidator is called with an undefined pattern
 */
export async function validateNodeDetails(architecture, schemaDirectory, debug, recursiveValidator, references) {
    const logger = initLogger(debug, 'validate-node-details');
    const jsonSchemaOutputs = [];
    const spectralOutputs = [];
    let hasErrors = false;
    let hasWarnings = false;
    const calmCore = tryParseCalmCore(architecture, logger);
    if (!calmCore) {
        return { jsonSchemaOutputs, spectralOutputs, hasErrors, hasWarnings };
    }
    const validateNodes = async () => {
        for (const [nodeIdx, node] of calmCore.nodes.entries()) {
            const result = await validateNodeDetail(node, nodeIdx, schemaDirectory, debug, recursiveValidator, references);
            jsonSchemaOutputs.push(...result.jsonSchemaOutputs);
            spectralOutputs.push(...result.spectralOutputs);
            if (result.hasErrors)
                hasErrors = true;
            if (result.hasWarnings)
                hasWarnings = true;
        }
    };
    const rootId = architecture['$id'];
    if (references.ancestry.length === 0 && typeof rootId === 'string' && rootId.length > 0) {
        await references.withAncestry(rootId, validateNodes);
    }
    else {
        await validateNodes();
    }
    return { jsonSchemaOutputs, spectralOutputs, hasErrors, hasWarnings };
}
async function validateNodeDetail(node, nodeIdx, schemaDirectory, debug, recursiveValidator, references) {
    const logger = initLogger(debug, 'validate-node-details');
    const detailedArchitecture = node.details?.detailedArchitecture;
    const archUrl = detailedArchitecture?.reference;
    if (!detailedArchitecture || !archUrl)
        return emptyNodeResult();
    const detailsPrefix = `/nodes/${nodeIdx}/details/detailed-architecture`;
    if (references.isInAncestry(archUrl)) {
        const cyclePath = references.formatCycle(archUrl);
        logger.debug(`Detected cyclic detailed-architecture reference: ${cyclePath}`);
        return nodeError(cycleDetectedError(detailsPrefix, cyclePath));
    }
    // A reference already resolved in a completed branch is skipped silently (sibling/diamond dedupe).
    // A reference whose previous load FAILED is deliberately not skipped, so the load error is
    // re-reported at every node that references it (the resolver returns the cached failure, so the
    // delegate is not re-fetched).
    if (references.has(archUrl) && !references.hasFailed(archUrl)) {
        logger.debug(`Skipping already-resolved architecture '${archUrl}'`);
        return emptyNodeResult();
    }
    const { subArch, error: loadError } = await loadSubArchitecture(references, archUrl, detailsPrefix);
    if (loadError)
        return nodeError(loadError);
    const pattern = await resolvePattern(node, subArch, schemaDirectory, logger);
    // A cache-seeded fork isolates AJV schema-id compilation for the sub-architecture while
    // still reusing the parent's already-loaded base schemas (no redundant re-fetch).
    const freshDir = schemaDirectory.fork();
    const { outcome, error: validationError } = await runRecursiveValidator(subArch, pattern, freshDir, debug, references, recursiveValidator, archUrl, detailsPrefix);
    if (validationError)
        return nodeError(validationError);
    return prefixedNodeResult(outcome, detailsPrefix);
}
/**
 * Load the raw sub-architecture document through the shared {@link CachingTrackingResolver}.
 * Validation needs the raw JSON document (for Spectral + JSON-Schema), and the resolver both
 * caches it and records the reference as visited.
 */
async function loadSubArchitecture(references, archUrl, detailsPrefix) {
    try {
        const subArch = await references.resolve(archUrl);
        return { subArch };
    }
    catch (err) {
        return { error: nodeDetailsError(detailsPrefix, `Could not load detailed-architecture '${archUrl}': ${getErrorMessage(err)}`) };
    }
}
async function resolvePattern(node, subArch, schemaDirectory, logger) {
    if (node.details?.requiredPattern?.reference) {
        const pattern = await tryLoadSchema(node.details.requiredPattern.reference, schemaDirectory, logger, 'required-pattern');
        if (pattern)
            return pattern;
    }
    const schemaRef = subArch['$schema'];
    if (schemaRef) {
        return tryLoadSchema(schemaRef, schemaDirectory, logger, '$schema');
    }
    return undefined;
}
async function tryLoadSchema(url, schemaDirectory, logger, label) {
    try {
        const schema = await schemaDirectory.getSchema(url);
        if (!schema)
            logger.debug(`Pattern from ${label} '${url}' not found in schema directory`);
        return schema;
    }
    catch (err) {
        logger.debug(`Could not load pattern from ${label} '${url}': ${getErrorMessage(err)}`);
        return undefined;
    }
}
async function runRecursiveValidator(subArch, pattern, freshDir, debug, references, recursiveValidator, archUrl, detailsPrefix) {
    try {
        const outcome = await references.withAncestry(archUrl, () => recursiveValidator(subArch, pattern, freshDir, debug, references));
        return { outcome };
    }
    catch (err) {
        return { error: nodeDetailsError(detailsPrefix, `Validation of detailed-architecture '${archUrl}' failed: ${getErrorMessage(err)}`) };
    }
}
function prefixedNodeResult(outcome, prefix) {
    return {
        jsonSchemaOutputs: prefixOutputs(outcome.jsonSchemaValidationOutputs, prefix),
        spectralOutputs: prefixOutputs(outcome.spectralSchemaValidationOutputs, prefix),
        hasErrors: outcome.hasErrors,
        hasWarnings: outcome.hasWarnings
    };
}
function prefixOutputs(outputs, prefix) {
    return outputs.map(output => {
        const newPath = output.path === '/' ? prefix : prefix + output.path;
        return new ValidationOutput(output.code, output.severity, output.message, newPath, output.schemaPath, output.line_start, output.line_end, output.character_start, output.character_end, output.source);
    });
}
function emptyNodeResult() {
    return { jsonSchemaOutputs: [], spectralOutputs: [], hasErrors: false, hasWarnings: false };
}
function nodeError(output) {
    return { jsonSchemaOutputs: [output], spectralOutputs: [], hasErrors: true, hasWarnings: false };
}
function nodeDetailsError(path, message) {
    return ValidationOutput.error('node-details-validation', message, path, { source: 'architecture' });
}
function cycleDetectedError(path, cyclePath) {
    return ValidationOutput.error('cycle-detected', `Cyclic detailed-architecture reference detected: ${cyclePath}`, path, { source: 'architecture' });
}
//# sourceMappingURL=validate-node-details.js.map